// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useRef } from 'react';

import type { UserSession } from '@/app/user-context';
import { getSharedBffWebSocket, UserInfoEvent } from '@/services/bffWebSocket';
import { createRequestId } from '@/services/requestId';

export const useUserInfoSync = (params: {
  apiBaseUrl: string;
  user: UserSession | null;
  setUser: (user: UserSession | null) => void;
}) => {
  const { apiBaseUrl, user, setUser } = params;
  const lastUserInfoRequestRef = useRef<{ userId?: string; sentAt?: number } | null>(null);
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    const client = getSharedBffWebSocket(apiBaseUrl);
    client.setAuthToken(user?.token ?? null);
    const unsubscribe = client.onMessage((message) => {
      if (message.type !== 'user.info') return;
      const info = message as UserInfoEvent;
      const current = userRef.current;
      if (!current || !info.userId || current.id !== info.userId) return;
      if (info.forename && info.forename !== current.forename) {
        setUser({ ...current, forename: info.forename });
      }
    });
    return () => unsubscribe();
  }, [apiBaseUrl, setUser, user?.token]);

  useEffect(() => {
    if (!user?.id || !user?.token) return;
    const last = lastUserInfoRequestRef.current;
    const now = Date.now();
    if (last?.userId === user.id && last.sentAt && now - last.sentAt < 5000) {
      return;
    }
    lastUserInfoRequestRef.current = { userId: user.id, sentAt: now };
    const client = getSharedBffWebSocket(apiBaseUrl);
    client.setAuthToken(user.token);
    // The BFF reads requestId as a Guid, so it has to be UUID-shaped (see services/requestId.ts).
    client.send({ type: 'user.view', userId: user.id, requestId: createRequestId() });
  }, [apiBaseUrl, user?.id, user?.token]);
};
